import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type {
  BoardOrderInput,
  BoardOrderResponse,
  InboxMessage,
  InboxResponse,
  MessagePatchInput,
  SendMessageInput,
  SendMessageResponse,
} from "../shared/contracts";
import { apiRequest } from "./api";

type InboxStatus = "idle" | "loading" | "ready" | "error";

export function useInbox(enabled: boolean, tenantId: string | undefined, mailboxId: string | undefined) {
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [status, setStatus] = useState<InboxStatus>(enabled ? "loading" : "idle");
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [hiddenRecipientEmails, setHiddenRecipientEmails] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const pendingLoadsRef = useRef(0);
  const nextCursorRef = useRef<string | null>(null);
  const initialPageLoadedRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const loadMoreControllerRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal, foreground = false) => {
      if (!enabled || !tenantId || !mailboxId) {
        setMessages([]);
        setHiddenRecipientEmails([]);
        nextCursorRef.current = null;
        initialPageLoadedRef.current = false;
        setNextCursor(null);
        setLoadMoreError(null);
        setStatus("idle");
        setSyncing(false);
        return null;
      }

      pendingLoadsRef.current += 1;
      setSyncing(true);
      if (foreground) {
        setStatus("loading");
        setError(null);
      }

      try {
        const response = await fetch("/api/v1/inbox", {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal,
        });

        if (response.status === 401) {
          window.dispatchEvent(new Event("powermail:session-expired"));
          return;
        }
        if (!response.ok) throw new Error(`A API respondeu com status ${response.status}.`);

        const payload = (await response.json()) as InboxResponse;
        const replaceCurrent = foreground || !initialPageLoadedRef.current;
        if (replaceCurrent) {
          setMessages(payload.data);
          nextCursorRef.current = payload.meta.nextCursor;
          setNextCursor(payload.meta.nextCursor);
          initialPageLoadedRef.current = true;
        } else {
          setMessages((current) => {
            const refreshedIds = new Set(payload.data.map((message) => message.id));
            return [...payload.data, ...current.filter((message) => !refreshedIds.has(message.id))];
          });
        }
        setHiddenRecipientEmails(payload.meta.hiddenRecipientEmails);
        setSyncedAt(payload.meta.syncedAt);
        setStatus("ready");
        setError(null);
        return payload.data;
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === "AbortError") return null;
        if (foreground) {
          setError(caught instanceof Error ? caught.message : "Falha ao carregar a caixa de entrada.");
          setStatus("error");
        }
        return null;
      } finally {
        pendingLoadsRef.current = Math.max(0, pendingLoadsRef.current - 1);
        if (pendingLoadsRef.current === 0) setSyncing(false);
      }
    },
    [enabled, mailboxId, tenantId],
  );

  useEffect(() => {
    loadMoreControllerRef.current?.abort();
    initialPageLoadedRef.current = false;
    nextCursorRef.current = null;
    loadingMoreRef.current = false;
    setMessages([]);
    setNextCursor(null);
    setLoadingMore(false);
    setLoadMoreError(null);
    const controller = new AbortController();
    void load(controller.signal, true);
    return () => {
      controller.abort();
      loadMoreControllerRef.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    if (!enabled || !tenantId || !mailboxId) return;
    const refreshWhenActive = () => {
      if (document.visibilityState === "visible" && navigator.onLine) void load();
    };
    const intervalId = window.setInterval(refreshWhenActive, 10_000);
    window.addEventListener("focus", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
    };
  }, [enabled, load, mailboxId, tenantId]);

  const patchMessage = useCallback(async (messageId: string, patch: MessagePatchInput) => {
    let previous: InboxMessage[] = [];
    setMessages((current) => {
      previous = current;
      const selected = current.find((message) => message.id === messageId);
      return current.map((message) => {
        const sameWorkItem = selected
          && message.threadId === selected.threadId
          && (patch.stage !== undefined || patch.responsibleId !== undefined || patch.dueAt !== undefined || patch.tagIds !== undefined);
        if (message.id !== messageId && !sameWorkItem) return message;
        return {
          ...message,
          ...(message.id === messageId && patch.unread !== undefined ? { unread: patch.unread } : {}),
          ...(message.id === messageId && patch.starred !== undefined ? { starred: patch.starred } : {}),
          ...(patch.stage !== undefined ? { stage: patch.stage } : {}),
          ...(message.id === messageId && patch.folder !== undefined ? { folder: patch.folder } : {}),
          ...(patch.dueAt !== undefined ? { dueAt: patch.dueAt } : {}),
        };
      });
    });

    try {
      const response = await apiRequest<{ data: InboxMessage }>(`/api/v1/messages/${encodeURIComponent(messageId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      setMessages((current) => current.map((message) => {
        if (message.id === messageId) return response.data;
        if (message.threadId !== response.data.threadId) return message;
        return {
          ...message,
          stage: response.data.stage,
          boardPosition: response.data.boardPosition,
          responsible: response.data.responsible,
          owner: response.data.owner,
          dueAt: response.data.dueAt,
          tags: response.data.tags,
          task: response.data.task,
        };
      }));
      return response.data;
    } catch (caught) {
      setMessages(previous);
      throw caught;
    }
  }, []);

  const reorderBoard = useCallback(async (input: BoardOrderInput) => {
    const optimistic = new Map(
      input.columns.flatMap((column) => column.messageIds.map((messageId, index) => [
        messageId,
        { stage: column.stage, position: (index + 1) * 1_024 },
      ] as const)),
    );
    let previous: InboxMessage[] = [];
    setMessages((current) => {
      previous = current;
      return current.map((message) => {
        const item = optimistic.get(message.id);
        return item ? { ...message, stage: item.stage, boardPosition: item.position } : message;
      });
    });

    try {
      const response = await apiRequest<BoardOrderResponse>("/api/v1/board/order", {
        method: "PUT",
        body: JSON.stringify(input),
      });
      const confirmed = new Map(response.items.map((item) => [item.messageId, item]));
      setMessages((current) => current.map((message) => {
        const item = confirmed.get(message.id);
        return item ? { ...message, stage: item.stage, boardPosition: item.position } : message;
      }));
      return response;
    } catch (caught) {
      setMessages(previous);
      throw caught;
    }
  }, []);

  const send = useCallback(async (input: SendMessageInput) => {
    const response = await apiRequest<SendMessageResponse>("/api/v1/messages/send", {
      method: "POST",
      body: JSON.stringify(input),
    });
    setMessages((current) => [response.data, ...current.filter((message) => message.id !== response.data.id)]);
    setSyncedAt(new Date().toISOString());
    return response;
  }, []);

  const hideRecipientSuggestion = useCallback(async (email: string) => {
    const normalized = email.trim().toLocaleLowerCase("en-US");
    let previous: string[] = [];
    setHiddenRecipientEmails((current) => {
      previous = current;
      return current.includes(normalized) ? current : [...current, normalized];
    });
    try {
      await apiRequest<void>(`/api/v1/recipient-suggestions/${encodeURIComponent(normalized)}`, {
        method: "DELETE",
      });
    } catch (caught) {
      setHiddenRecipientEmails(previous);
      throw caught;
    }
  }, []);

  const loadMore = useCallback(async () => {
    const cursor = nextCursorRef.current;
    if (!enabled || !tenantId || !mailboxId || !cursor || loadingMoreRef.current) return null;

    loadMoreControllerRef.current?.abort();
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);

    try {
      const response = await fetch(`/api/v1/inbox?cursor=${encodeURIComponent(cursor)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (response.status === 401) {
        window.dispatchEvent(new Event("powermail:session-expired"));
        return null;
      }
      if (!response.ok) throw new Error(`A API respondeu com status ${response.status}.`);

      const payload = (await response.json()) as InboxResponse;
      setMessages((current) => {
        const currentIds = new Set(current.map((message) => message.id));
        return [...current, ...payload.data.filter((message) => !currentIds.has(message.id))];
      });
      nextCursorRef.current = payload.meta.nextCursor;
      setNextCursor(payload.meta.nextCursor);
      setSyncedAt(payload.meta.syncedAt);
      return payload.data;
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return null;
      setLoadMoreError(caught instanceof Error ? caught.message : "Falha ao carregar e-mails anteriores.");
      return null;
    } finally {
      if (loadMoreControllerRef.current === controller) {
        loadMoreControllerRef.current = null;
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [enabled, mailboxId, tenantId]);

  const retry = useCallback(() => {
    void load();
  }, [load]);

  const refresh = useCallback(() => load(), [load]);

  return {
    messages,
    setMessages,
    status,
    syncing,
    error,
    hiddenRecipientEmails,
    syncedAt,
    hasMore: nextCursor !== null,
    loadingMore,
    loadMoreError,
    retry,
    refresh,
    loadMore,
    patchMessage,
    reorderBoard,
    hideRecipientSuggestion,
    send,
  };
}
